(function () {
  'use strict';

  if (customElements.get('jmt-first-pick-purchase')) return;

  class JmtFirstPickPurchase extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.product = this.readProduct();
      this.modal = document.getElementById(this.dataset.modalId);
      this.openButton = this.querySelector('[data-jmt-first-pick-open]');
      this.price = this.modal && this.modal.querySelector('[data-jmt-first-pick-price]');
      this.selects = this.modal ? Array.from(this.modal.querySelectorAll('[data-jmt-first-pick-option]')) : [];
      this.variantInput = this.modal && this.modal.querySelector('[data-jmt-first-pick-variant-id]');
      this.submitButton = this.modal && this.modal.querySelector('[data-jmt-first-pick-submit]');
      this.submitLabel = this.modal && this.modal.querySelector('[data-jmt-first-pick-submit-label]');

      this.onOpen = this.open.bind(this);
      this.onOptionChange = this.updateVariant.bind(this);
      this.onPreferenceChange = this.applyPreference.bind(this);

      if (this.openButton) this.openButton.addEventListener('click', this.onOpen);
      this.selects.forEach((select) => select.addEventListener('change', this.onOptionChange));
      window.addEventListener('jmt:pref:change', this.onPreferenceChange);

      this.updateVariant();
      this.waitForPreferenceEngine();
      this.waitForCartSubscription();
    }

    disconnectedCallback() {
      if (this.openButton) this.openButton.removeEventListener('click', this.onOpen);
      this.selects.forEach((select) => select.removeEventListener('change', this.onOptionChange));
      window.removeEventListener('jmt:pref:change', this.onPreferenceChange);
      window.clearTimeout(this.preferenceTimer);
      window.clearTimeout(this.subscriptionTimer);
      if (this.unsubscribeCart) this.unsubscribeCart();
      this.initialized = false;
    }

    readProduct() {
      const node = this.querySelector('[data-jmt-first-pick-product-json]');
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        return null;
      }
    }

    open(event) {
      if (!this.modal || typeof this.modal.show !== 'function') return;
      this.applyPreference();
      this.modal.show(event?.currentTarget || this.openButton);
      this.publishAnalytics('jmt:first_thong_product_modal_open', {
        product_id: this.dataset.productId || '',
        product_title: this.dataset.productTitle || '',
        page_path: window.location.pathname || ''
      });
    }

    waitForPreferenceEngine(attempt) {
      const tries = Number(attempt || 0);
      if (window.JMT_PREF && window.JMT_VARIANTS) {
        this.applyPreference();
        return;
      }
      if (tries >= 30) return;
      this.preferenceTimer = window.setTimeout(() => this.waitForPreferenceEngine(tries + 1), 100);
    }

    applyPreference() {
      if (!window.JMT_PREF || !window.JMT_VARIANTS || !this.selects.length) return;
      const preference = window.JMT_PREF.get();
      if (!preference) return;

      this.selects.forEach((select) => {
        const optionName = select.dataset.optionName || select.name.replace(/^options\[|\]$/g, '');
        const wanted = window.JMT_VARIANTS.wantedValue(preference, optionName);
        if (!wanted) return;
        const match = Array.from(select.options).find((option) => {
          return window.JMT_VARIANTS.normalizeValue(optionName, option.value) ===
            window.JMT_VARIANTS.normalizeValue(optionName, wanted);
        });
        if (match) {
          select.value = match.value;
          select.dataset.jmtLastRealValue = match.value;
        }
      });

      this.updateVariant();
    }

    selections() {
      return this.selects.reduce((values, select) => {
        const optionName = select.dataset.optionName || select.name.replace(/^options\[|\]$/g, '');
        values[optionName] = select.value;
        return values;
      }, {});
    }

    findVariant() {
      if (!this.product) return null;
      if (!this.selects.length) return this.product.variants && this.product.variants[0];

      if (window.JMT_VARIANTS) {
        return window.JMT_VARIANTS.findVariant(this.product, this.selections(), { requireAll: true });
      }

      const selected = this.selects.map((select) => String(select.value));
      return (this.product.variants || []).find((variant) => {
        return variant.options.every((value, index) => String(value) === selected[index]);
      }) || null;
    }

    updateVariant() {
      const variant = this.findVariant();
      this.updateOptionAvailability();

      if (this.variantInput) {
        this.variantInput.value = variant ? variant.id : '';
        this.variantInput.disabled = !variant || variant.available === false;
      }

      if (this.price && variant) this.price.textContent = this.formatMoney(variant.price);

      if (!this.submitButton || !this.submitLabel) return;
      const available = Boolean(variant && variant.available !== false);
      this.submitButton.disabled = !available;
      this.submitButton.toggleAttribute('aria-disabled', !available);
      this.submitLabel.textContent = available
        ? `${this.dataset.addLabel || 'Add to cart'} · ${this.formatMoney(variant.price, false)}`
        : (variant ? this.dataset.soldOutLabel : this.dataset.unavailableLabel) || 'Unavailable';
    }

    updateOptionAvailability() {
      if (!window.JMT_VARIANTS || !this.product || !this.selects.length) return;
      const selections = this.selections();
      this.selects.forEach((select) => {
        const optionName = select.dataset.optionName || select.name.replace(/^options\[|\]$/g, '');
        const availableValues = window.JMT_VARIANTS.availableValues(this.product, optionName, selections);
        Array.from(select.options).forEach((option) => {
          if (
            option.hasAttribute('data-jmt-guide-option') ||
            /^__JMT_(?:POUCH|STRAP)_GUIDE__$/.test(String(option.value || ''))
          ) {
            option.disabled = false;
            return;
          }
          option.disabled = !availableValues[window.JMT_VARIANTS.norm(option.value)];
        });
      });
    }

    formatMoney(cents, includeCurrency) {
      const amount = Number(cents || 0) / 100;
      const currency = (this.product && this.product.currency) || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency,
          currencyDisplay: includeCurrency === false ? 'symbol' : 'code'
        }).format(amount);
      } catch (error) {
        return `$${amount.toFixed(2)}${includeCurrency === false ? '' : ` ${currency}`}`;
      }
    }

    waitForCartSubscription(attempt) {
      const tries = Number(attempt || 0);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (!event || event.source !== 'product-form') return;
          if (String(event.productVariantId || '') !== String(this.variantInput && this.variantInput.value || '')) return;
          this.publishAnalytics('jmt:first_customer_product_add', {
            product_id: this.dataset.productId || '',
            product_title: this.dataset.productTitle || '',
            variant_id: String(this.variantInput && this.variantInput.value || ''),
            page_path: window.location.pathname || ''
          });
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'jmt_first_customer_product_add',
            product_id: this.dataset.productId || '',
            product_title: this.dataset.productTitle || '',
            variant_id: String(this.variantInput && this.variantInput.value || ''),
            page_path: window.location.pathname || ''
          });
          if (this.modal && this.modal.hasAttribute('open')) this.modal.hide();
        });
        return;
      }
      if (tries >= 30) return;
      this.subscriptionTimer = window.setTimeout(() => this.waitForCartSubscription(tries + 1), 100);
    }

    publishAnalytics(eventName, payload) {
      try {
        if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
          Shopify.analytics.publish(eventName, payload);
        }
      } catch (error) {}
    }
  }

  customElements.define('jmt-first-pick-purchase', JmtFirstPickPurchase);
})();
